import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { AlertTriangle, CheckCircle2, Download, Eye, FileDown, FileUp, Loader2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import type { CommitteePaperItem, CommitteePaperVersionItem } from '@/hooks/use-committee-papers';
import { useCommitteePaperVersions, useUploadCleanVersion } from '@/hooks/use-committee-papers';
import api from '@/api';

interface PaperDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  paper: CommitteePaperItem | null;
}

const CLEAN_ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const CLEAN_MAX_SIZE_BYTES = 20 * 1024 * 1024;

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function typeBadgeClass(versionType: CommitteePaperVersionItem['versionType']): string {
  switch (versionType) {
    case 'CLEAN':
      return 'rounded-md border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800';
    case 'REVISION':
      return 'rounded-md border border-yellow-200 bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800';
    default:
      return 'rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700';
  }
}

function normalizeFilename(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

function resolveSourceRound(version: CommitteePaperVersionItem): number {
  if (version.versionType === 'ORIGINAL') return 1;
  return (version.roundNumber ?? 1) + 1;
}

export function PaperDocumentDialog({ open, onOpenChange, eventId, paper }: PaperDocumentDialogProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadLiveMessage, setUploadLiveMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const paperId = paper?.id || '';
  const versionsQuery = useCommitteePaperVersions(paperId, open && Boolean(paperId));
  const uploadMutation = useUploadCleanVersion(eventId, paperId);

  const versions = versionsQuery.data || [];
  const latestCleanVersion = useMemo(
    () =>
      versions
        .filter((item) => item.versionType === 'CLEAN')
        .sort((a, b) => b.versionNumber - a.versionNumber)[0] || null,
    [versions],
  );
  const latestRevisionVersion = useMemo(
    () =>
      versions
        .filter((item) => item.versionType === 'REVISION')
        .sort((a, b) => b.versionNumber - a.versionNumber)[0] || null,
    [versions],
  );
  const needsCleanAfterRevision = Boolean(
    latestRevisionVersion && (!latestCleanVersion || latestRevisionVersion.versionNumber > latestCleanVersion.versionNumber),
  );
  const sourceDocuments = useMemo(
    () =>
      versions
        .filter((item) => item.versionType === 'ORIGINAL' || item.versionType === 'REVISION')
        .map((item) => ({
          version: item,
          sourceRound: resolveSourceRound(item),
        }))
        .sort((a, b) => a.sourceRound - b.sourceRound || a.version.versionNumber - b.version.versionNumber),
    [versions],
  );
  const currentSourceDocument = sourceDocuments[sourceDocuments.length - 1] || null;

  const handleDownload = async (version: CommitteePaperVersionItem) => {
    if (!paper) return;

    try {
      const response = await api.get(`/committee/papers/${paper.id}/versions/${version.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = normalizeFilename(version.archivoNombre, `${paper.folio || 'ponencia'}_v${version.versionNumber}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('No se pudo descargar el archivo.', {
        description: error?.response?.data?.message || 'Intente nuevamente.',
      });
    }
  };

  const uploadClean = async (file: File) => {
    setUploadError(null);

    if (!CLEAN_ALLOWED_MIME_TYPES.has(file.type)) {
      const message = 'Solo se permiten archivos PDF o DOCX.';
      setUploadError(message);
      toast.error('Tipo de archivo no permitido.', { description: message });
      return;
    }

    if (file.size > CLEAN_MAX_SIZE_BYTES) {
      const message = 'El archivo excede el limite de 20MB.';
      setUploadError(message);
      toast.error('Archivo demasiado grande.', { description: message });
      return;
    }

    try {
      setUploadLiveMessage('Subiendo documento CLEAN...');
      await uploadMutation.mutateAsync(file);
      setUploadLiveMessage('Documento CLEAN cargado correctamente.');
      toast.success('Version CLEAN subida correctamente.');
    } catch (error: any) {
      const message = error?.response?.data?.message || 'No fue posible subir la version CLEAN.';
      setUploadError(message);
      setUploadLiveMessage('Error al subir documento CLEAN.');
      toast.error('Error de carga', { description: message });
    }
  };

  const handleDrop = async (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await uploadClean(file);
  };

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadClean(file);
    event.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[100vh] w-full max-w-full overflow-y-auto rounded-none p-0 sm:max-h-[92vh] sm:max-w-4xl sm:rounded-xl xl:max-w-6xl 2xl:max-w-7xl">
        <DialogHeader className="border-b border-slate-200 px-6 py-4 text-left xl:px-8">
          <DialogTitle className="text-3xl font-bold text-[#0F2644]">Gestion de Documentos de Ponencia</DialogTitle>
          <DialogDescription>
            {paper ? `${paper.folio} - ${paper.title}` : 'Consulte y gestione versiones del documento de ponencia.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 p-5 sm:gap-6 sm:p-6 xl:p-8">
          <section className="rounded-md border border-slate-200 bg-white p-5">
            <h3 className="mb-4 text-4xl font-bold text-slate-800">Documento CLEAN</h3>

            {versionsQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-44 w-full" />
              </div>
            ) : versionsQuery.isError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                No se pudo cargar el historial de versiones.
              </div>
            ) : latestCleanVersion && !needsCleanAfterRevision ? (
              <div className="w-full rounded-md border border-green-200 bg-green-50 p-4 text-green-800">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" />
                  <p className="text-lg">
                    Version CLEAN disponible. <span className="font-bold">(v{latestCleanVersion.versionNumber})</span> La
                    ultima version de este documento ha sido validada y esta lista para su uso.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />
                    <div>
                      <p className="text-lg font-semibold">
                        {needsCleanAfterRevision ? 'REVISION recibida: falta CLEAN v2' : 'No existe version anonimizada'}
                      </p>
                      <p className="text-sm">
                        {needsCleanAfterRevision
                          ? 'Revise la version corregida del autor y cargue/genere CLEAN v2 antes de reactivar evaluacion.'
                          : 'Debe cargar una version CLEAN antes de asignar evaluadores.'}
                      </p>
                    </div>
                  </div>
                </div>

                <label
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`cursor-pointer rounded-md border-2 border-dashed p-8 text-center transition-colors duration-200 focus-within:ring-2 focus-within:ring-slate-400 ${
                    dragOver ? 'border-slate-500 bg-slate-100' : 'border-slate-300 bg-transparent hover:bg-slate-50'
                  }`}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="sr-only"
                    onChange={handleInputChange}
                    disabled={uploadMutation.isPending}
                  />
                  <div className="flex flex-col items-center justify-center">
                    <UploadCloud className="mb-3 h-8 w-8 text-slate-400" />
                    <p className="text-2xl font-semibold text-slate-700">Subir version CLEAN</p>
                    <p className="mt-1 text-sm text-slate-500">Arrastra y suelta un archivo aqui o haz clic para buscar</p>
                    <span className="mt-4 inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-50">
                      <FileUp className="h-4 w-4" />
                      Adjuntar Archivo
                    </span>
                  </div>
                </label>
              </div>
            )}

            <div aria-live="polite" className="mt-4 text-sm">
              {uploadMutation.isPending ? (
                <span className="inline-flex items-center gap-2 text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Subiendo archivo...
                </span>
              ) : null}
              {!uploadMutation.isPending && uploadLiveMessage ? <span className="text-slate-600">{uploadLiveMessage}</span> : null}
            </div>

            {uploadError ? (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{uploadError}</div>
            ) : null}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-5">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="mb-1 text-4xl font-bold text-slate-800">Documento fuente vigente</h3>
                <p className="text-sm text-slate-500">
                  Descarga el Word fuente del ponente para la ronda operativa actual.
                </p>
              </div>
              {currentSourceDocument ? (
                <span className="w-fit rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-800">
                  Ronda vigente: Ronda {currentSourceDocument.sourceRound}
                </span>
              ) : null}
            </div>

            {versionsQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-10 w-40" />
              </div>
            ) : currentSourceDocument ? (
              <div className="space-y-4">
                <div className="rounded-md border border-blue-100 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Fuente actual
                  </p>
                  <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {normalizeFilename(
                          currentSourceDocument.version.archivoNombre,
                          `${paper?.folio || 'ponencia'}_fuente_ronda_${currentSourceDocument.sourceRound}`,
                        )}
                      </p>
                      <p className="text-sm text-slate-600">
                        Ronda {currentSourceDocument.sourceRound} · {currentSourceDocument.version.versionType}
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleDownload(currentSourceDocument.version)}
                      aria-label={`Descargar documento fuente vigente de ronda ${currentSourceDocument.sourceRound}`}
                      className="bg-[#0F2644] text-white transition-colors duration-200 hover:bg-[#17345b]"
                    >
                      <FileDown className="mr-2 h-4 w-4" />
                      Descargar fuente vigente
                    </Button>
                  </div>
                </div>

                {sourceDocuments.length > 1 ? (
                  <div>
                    <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-600">
                      Documentos fuente por ronda
                    </h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {sourceDocuments.map(({ version, sourceRound }) => {
                        const isCurrent = currentSourceDocument.version.id === version.id;
                        return (
                          <div
                            key={`${version.id}-${sourceRound}`}
                            className={`rounded-md border p-3 ${
                              isCurrent ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'
                            }`}
                          >
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <div>
                                <p className="font-semibold text-slate-900">Ronda {sourceRound}</p>
                                <p className="max-w-[220px] truncate text-sm text-slate-600">
                                  {normalizeFilename(version.archivoNombre, `${paper?.folio || 'ponencia'}_ronda_${sourceRound}`)}
                                </p>
                              </div>
                              {isCurrent ? (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                                  Vigente
                                </span>
                              ) : null}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => handleDownload(version)}
                              aria-label={`Descargar documento fuente de ronda ${sourceRound}`}
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Descargar Ronda {sourceRound}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No hay documento fuente disponible para descarga.
              </div>
            )}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-5">
            <h3 className="mb-4 text-4xl font-bold text-slate-800">Historial de Versiones</h3>

            {versionsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : versionsQuery.isError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                Error al cargar historial de versiones.
              </div>
            ) : versions.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
                No existen versiones de documento para esta ponencia.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Fecha de Carga</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Restricciones/Comentarios</TableHead>
                      <TableHead>Accion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versions.map((version) => (
                      <TableRow key={version.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <TableCell className="font-semibold">v{version.versionNumber}</TableCell>
                        <TableCell>{formatDate(version.uploadedAt)}</TableCell>
                        <TableCell>{version.uploadedBy || '-'}</TableCell>
                        <TableCell>
                          <span className={typeBadgeClass(version.versionType)}>{version.versionType}</span>
                        </TableCell>
                        <TableCell>{version.roundNumber ? `Ronda ${version.roundNumber}` : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => handleDownload(version)}
                              aria-label={`Descargar version v${version.versionNumber}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => handleDownload(version)}
                              aria-label={`Ver o descargar version v${version.versionNumber}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
